import { NotificationSeverity } from '@/components/notifications/Notification';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useBatchTransitions } from './useBatchTransitions';
import { useScannerStorage } from './useScannerStorage';
import { getScannedCode } from '@/utils/scannedCode';
import { MAX_SHIPPINGS_PER_BATCH } from '../../config/batchLimits';
import { useState, useCallback } from 'react';
import { useNotification } from '@/hooks';
import {
  useValidateBatchShippingMutation,
  useExecuteBatchTransitionBulkMutation,
} from '../../services/batchCheckpoints.endpoints';

type UseBatchTransitionsQueryHook = Parameters<typeof useBatchTransitions>[1];

interface UseBatchCheckpointsScannerOptions {
  useTransitionsQuery?: UseBatchTransitionsQueryHook;
}

export const useBatchCheckpointsScanner = (
  { useTransitionsQuery }: UseBatchCheckpointsScannerOptions = {}
) => {
  const { showNotification } = useNotification();
  const {
    transition,
    setTransition,
    scannedItems,
    setScannedItems,
    clearStorage,
    clearItems,
    removeItem,
  } = useScannerStorage();

  const [inputValue, setInputValue] = useState('');
  const [isScannerActive, setIsScannerActive] = useState(true);
  const [isCheckpointWarningModalOpen, setIsCheckpointWarningModalOpen] = useState(false);
  const [batchFailures, setBatchFailures] = useState<{ shippingNumber: string; reason: string }[]>([]);
  const [scanError, setScanError] = useState<{ message: string; shippingCode: string } | null>(null);
  const selectedTransitionId = transition?.data as string | undefined;

  const {
    transitionOptions,
    isTransitionsLoading,
    logisticOperatorId,
    includedStatuses,
    isLoadingStatuses,
  } = useBatchTransitions(selectedTransitionId, useTransitionsQuery);

  const [validateShipping, { isLoading: isValidating }] =
    useValidateBatchShippingMutation();
  const [executeBatch, { isLoading: isExecuting }] =
    useExecuteBatchTransitionBulkMutation();

  const handleSetTransition = useCallback(
    (option: Option | null) => {
      setTransition(option);
      if (option) {
        (document.activeElement as HTMLElement)?.blur();
        setIsCheckpointWarningModalOpen(true);
      }
    },
    [setTransition]
  );

  const dismissCheckpointWarning = useCallback(() => {
    setIsCheckpointWarningModalOpen(false);
  }, []);

  const handleScan = useCallback(
    async (code: string) => {
      const originalCode = code.trim();
      if (!originalCode || !transition) return;

      if (scannedItems.length >= MAX_SHIPPINGS_PER_BATCH) {
        setScanError({
          message: `Llegaste al máximo de ${MAX_SHIPPINGS_PER_BATCH} pedidos por tanda. Aplicá los cambios de estado antes de seguir escaneando.`,
          shippingCode: originalCode,
        });
        return;
      }

      const scanned = getScannedCode(originalCode);
      if (!scanned) {
        setScanError({ message: 'Código inválido', shippingCode: originalCode });
        return;
      }
      const { code: shippingCode, mode } = scanned;

      const alreadyScanned = scannedItems.some(
        (item) => item.number === shippingCode || item.externalId === shippingCode
      );
      if (alreadyScanned) {
        showNotification(
          'Este pedido ya fue escaneado en esta sesión',
          NotificationSeverity.warning
        );
        return;
      }

      try {
        const result = await validateShipping({
          transitionId: transition.data as string,
          shippingNumber: shippingCode,
          mode,
          logisticOperatorId,
        }).unwrap();

        if (result.success) {
          const alreadyResolved = scannedItems.some(
            (item) => item.number === String(result.number)
          );
          if (alreadyResolved) {
            showNotification(
              'Este pedido ya fue escaneado en esta sesión',
              NotificationSeverity.warning
            );
            return;
          }

          const newItem = {
            number: String(result.number),
            externalId: result.externalId,
            serviceType: result.serviceType,
            customerName: result.customerName,
            commerceName: result.commerceName,
            destinationAddress: result.destinationAddress,
            scannedDt: new Date().toISOString(),
            scanOrder: scannedItems.length + 1,
          };
          setScannedItems((prev) => [newItem, ...prev]);
          showNotification(
            `Pedido ${result.number} validado correctamente`,
            NotificationSeverity.success
          );
        } else {
          setScanError({ message: result.message || 'El pedido no puede ser procesado en esta transición', shippingCode });
        }
      } catch (err) {
        const error = err as { data?: { message?: string }; message?: string };
        setScanError({ message: error.data?.message || error.message || 'Error al validar el pedido', shippingCode });
      }
    },
    [transition, logisticOperatorId, scannedItems, validateShipping, showNotification, setScannedItems]
  );

  const handleExecuteBatch = useCallback(async () => {
    if (!transition || scannedItems.length === 0) return;

    if (scannedItems.length > MAX_SHIPPINGS_PER_BATCH) {
      showNotification(
        `No se pueden aplicar más de ${MAX_SHIPPINGS_PER_BATCH} pedidos por tanda. Quitá pedidos de la lista antes de aplicar.`,
        NotificationSeverity.error
      );
      return;
    }

    try {
      const result = await executeBatch({
        transitionId: transition.data as string,
        codes: [...scannedItems].reverse().map((item) => Number(item.number)),
      }).unwrap();

      if (result.success) {
        showNotification(result.message, NotificationSeverity.success);
        clearStorage();
      } else {
        const failedNumbers = new Set(result.failed.map((failure) => failure.code));
        setScannedItems((prev) => prev.filter((item) => failedNumbers.has(item.number)));
        setBatchFailures(
          result.failed.map((failure) => ({ shippingNumber: failure.code, reason: failure.reason }))
        );
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const message = error.data?.message || error.message || 'Error al aplicar los cambios de estado';
      showNotification(message, NotificationSeverity.error);
    }
  }, [transition, scannedItems, executeBatch, showNotification, clearStorage, setScannedItems]);

  const isScanReady = !!transition;

  const handleRemoveItem = useCallback(
    (number: string) => removeItem(number),
    [removeItem]
  );

  return {
    transition,
    setTransition: handleSetTransition,
    transitionOptions,
    isTransitionsLoading,
    scannedItems,
    inputValue,
    setInputValue,
    isScannerActive,
    setIsScannerActive,
    isProcessing: isValidating || isExecuting,
    isScanReady,
    handleScan,
    handleExecuteBatch,
    handleRemoveItem,
    handleClearItems: clearItems,
    includedStatuses,
    isLoadingStatuses,
    isTransitionLocked: scannedItems.length > 0,
    isCheckpointWarningModalOpen,
    dismissCheckpointWarning,
    batchFailures,
    dismissBatchFailures: () => setBatchFailures([]),
    scanError,
    dismissScanError: () => setScanError(null),
  };
};
